import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle, Circle } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { getConversations } from '../services/api';
import { useDefaultAvatar } from '../services/defaultAvatar';
import { formatDistanceToNow } from 'date-fns';

const MessagesPage = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      const response = await getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const defaultPhoto = useDefaultAvatar() || 'https://images.unsplash.com/photo-1650702970095-f5b8ebeebce5?w=200';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center pb-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#009E60] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24" data-testid="messages-page">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-lg mx-auto px-4 py-3">
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
            Messages
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="pt-16">
        <div className="max-w-lg mx-auto">
          {conversations.length === 0 ? (
            <div className="text-center py-16 px-4">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-10 h-10 text-gray-300" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                No messages yet
              </h3>
              <p className="text-gray-500 mb-4">
                Match with someone to start chatting!
              </p>
              <button
                onClick={() => navigate('/discover')}
                className="text-[#009E60] font-semibold hover:underline"
              >
                Find Matches
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {conversations.map((conv, index) => (
                <motion.div
                  key={conv.match_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/messages/${conv.match_id}`)}
                  className="flex items-center gap-4 px-4 py-4 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                  data-testid={`conversation-${conv.match_id}`}
                >
                  <div className="relative">
                    <img
                      src={conv.user?.photos?.[0] || defaultPhoto}
                      alt={conv.user?.name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                    {conv.unread_count > 0 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#009E60] rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">{conv.unread_count}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className={`font-semibold ${conv.unread_count > 0 ? 'text-gray-900' : 'text-gray-700'}`}>
                        {conv.user?.name}
                      </h3>
                      {conv.last_message && (
                        <span className="text-xs text-gray-400">
                          {formatDistanceToNow(new Date(conv.last_message.created_at), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                      {conv.last_message?.content || 'Start a conversation'}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default MessagesPage;
